import { useTranslate } from '@tolgee/react';
import React, { useRef } from 'react';
import styled from 'styled-components';

import { useMetrics } from '../../hooks/useMetrics';
import { getFiles } from '../../utils/fileDropUtils';
import { isIos } from '../../utils/navigator';
import PresentationFileDrop from './PresentationFileDrop/PresentationFileDrop';
import PresentationFileDropV2 from './PresentationFileDrop/PresentationFileDropV2';

const validFileTypes = [
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', //pptx
  'key',
  'application/pdf',
];

export const PresentationFileSelector = (props: {
  onFileSelected: (file: File) => void;
  metricsName?: string;
}) => {
  const { t } = useTranslate();
  const { onFileSelected, metricsName } = props;

  const metrics = useMetrics();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files) {
      onFileSelected(files[0]);
    }
  };

  const handleActivate = () => {
    metrics.logEvent(metricsName || 'selectFile');

    if (ref.current) {
      ref.current.value = '';
      ref.current!.click();
    }
  };

  const ref = useRef<HTMLInputElement>(null);

  const handleFilesDropped = (
    files: FileList | null,
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    if (files) {
      const parsedFiles = getFiles(files);
      const invalidFiles = parsedFiles.filter(
        file => !validFileTypes.includes(file.type) && !file.name.endsWith('.key'),
      );
      if (invalidFiles.length > 0) {
        alert(t('Invalid file type, please try again'));
        return;
      }

      if (parsedFiles.length > 0) {
        metrics.logEvent('Creator.ImportPresentation.DropFile', {
          count: parsedFiles.length,
          type: parsedFiles[0].type,
          size: parsedFiles[0].size,
          name: parsedFiles[0].name,
        });
        onFileSelected(parsedFiles[0]);
      }
    }
  };
  return (
    <>
      <input
        data-testid="FileInput"
        ref={ref}
        type="file"
        name="myFile"
        multiple={false}
        //TODO: Re-think where we check filetype to allow for consistency across devices
        accept={isIos ? '*' : '.pptx, .key, .ppt, .pdf'}
        onChange={onChange}
        style={{ display: 'none' }}
      />
      <ChildWrapper
        data-testid="FileInputClickButton"
        tabIndex={0}
        onKeyPress={e => {
          if (e.key === 'Enter' || e.key === 'space') {
            handleActivate();
          }
        }}
        onClick={handleActivate}
      >
        <PresentationFileDrop onFilesDropped={handleFilesDropped} />
      </ChildWrapper>
    </>
  );
};

export const PresentationFileSelectorV2 = (props: {
  onFileSelected: (file: File) => void;
  metricsName?: string;
}) => {
  const { t } = useTranslate();
  const { onFileSelected, metricsName } = props;

  const metrics = useMetrics();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files) {
      onFileSelected(files[0]);
    }
  };

  const handleActivate = () => {
    metrics.logEvent(metricsName || 'selectFile');

    if (ref.current) {
      ref.current.value = '';
      ref.current!.click();
    }
  };

  const ref = useRef<HTMLInputElement>(null);

  const handleFilesDropped = (
    files: FileList | null,
    event: React.DragEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    if (files) {
      const parsedFiles = getFiles(files);
      const invalidFiles = parsedFiles.filter(
        file => !validFileTypes.includes(file.type) && !file.name.endsWith('.key'),
      );
      if (invalidFiles.length > 0) {
        alert(t('Invalid file type, please try again'));
        return;
      }

      if (parsedFiles.length > 0) {
        metrics.logEvent('Creator.ImportPresentation.DropFile', {
          count: parsedFiles.length,
          type: parsedFiles[0].type,
          size: parsedFiles[0].size,
          name: parsedFiles[0].name,
        });
        onFileSelected(parsedFiles[0]);
      }
    }
  };
  return (
    <>
      <input
        data-testid="FileInput"
        ref={ref}
        type="file"
        name="myFile"
        multiple={false}
        //TODO: Re-think where we check filetype to allow for consistency across devices
        accept={isIos ? '*' : '.pptx, .key, .ppt, .pdf'}
        onChange={onChange}
        style={{ display: 'none' }}
      />
      <ChildWrapper
        data-testid="FileInputClickButton"
        tabIndex={0}
        onKeyPress={e => {
          if (e.key === 'Enter' || e.key === 'space') {
            handleActivate();
          }
        }}
        onClick={handleActivate}
      >
        <PresentationFileDropV2 onFilesDropped={handleFilesDropped} />
      </ChildWrapper>
    </>
  );
};
export const PresentationFileSelectorV3 = (props: {
  onFileSelected: (file: File) => void;
  metricsName?: string;
  children?: React.ReactNode;
}) => {
  const { onFileSelected, metricsName } = props;

  const metrics = useMetrics();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files) {
      onFileSelected(files[0]);
    }
  };

  const handleActivate = () => {
    metrics.logEvent(metricsName || 'selectFile');

    if (ref.current) {
      ref.current.value = '';
      ref.current!.click();
    }
  };

  const ref = useRef<HTMLInputElement>(null);

  return (
    <>
      <input
        data-testid="FileInput"
        ref={ref}
        type="file"
        name="myFile"
        multiple={false}
        //TODO: Re-think where we check filetype to allow for consistency across devices
        accept={isIos ? '*' : '.pptx, .key, .ppt, .pdf'}
        onChange={onChange}
        style={{ display: 'none' }}
      />
      <ChildWrapper
        data-testid="FileInputClickButton"
        tabIndex={0}
        onKeyPress={e => {
          if (e.key === 'Enter' || e.key === 'space') {
            handleActivate();
          }
        }}
        onClick={handleActivate}
      >
        <>{props.children}</>
      </ChildWrapper>
    </>
  );
};
const ChildWrapper = styled.div`
  width: inherit;
  height: inherit;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  &:focus-within {
    outline: none;
  }
`;

export default PresentationFileSelector;
